import { useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Navigate, useDateFns, useNavigation, useProvider } from '~/Hooks';
import { IPresenceUpdate, PresenceType } from '~/Models/DTO/Booking.models';
import { requestChangeCheckinStatus, requestUpdateMultipleBookingPresence } from '~/store';
import { selectBookedListLength, selectBookingsList, selectIsCheckInOpen } from '~/store/bookings';
import { requestEventGroupById, selectEventById } from '~/store/events';
import { useModalHelper } from '~/Hooks/useModal';
import { DifferenceFormat } from '~/Models/DTO/Time.model';

export const useBookingListModals = () => {
  const dispatch = useDispatch();
  const { navigate, getParams } = useNavigation();
  const theme = useTheme();
  const { currentProvider } = useProvider();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));

  const { id, domain } = getParams();
  const selectedEvent = useSelector(selectEventById(id || ''));
  const totalBookings = useSelector(selectBookedListLength);
  const isCheckInOpen = useSelector(selectIsCheckInOpen(id || ''));
  const bookingList = useSelector(selectBookingsList);

  const updatePresences = bindActionCreators(requestUpdateMultipleBookingPresence, dispatch);
  const updateCheckInStatus = bindActionCreators(requestChangeCheckinStatus, dispatch);

  const presentBookings = useMemo(
    () => bookingList.filter((booking) => booking.presenceType === PresenceType.PRESENT).length,
    [bookingList],
  );
  const {
    isOpen: showCheckInCloseDialog,
    close: closeCheckInCloseDialog,
    open: openCheckInCloseDialog,
  } = useModalHelper(false);

  const {
    isOpen: showBookingDialog,
    toggle: toggleBookingDialog,
    close: closeBookingDialog,
    setOpen: setShowBookingDialog,
  } = useModalHelper(false);

  useEffect(() => {
    if (isCheckInOpen === undefined) {
      updateCheckInStatus(true, id || '');
    }
  }, []);

  const handleCloseCheckIn = () => {
    const bookingsToBeAbsent = bookingList.reduce((list, booking) => {
      if (booking.presenceType === PresenceType.NONE) {
        list.push({
          bookingId: booking.id,
          presenceType: PresenceType.ABSENT,
        });
      }
      return list;
    }, [] as IPresenceUpdate[]);
    updatePresences(bookingsToBeAbsent);
    updateCheckInStatus(false, id || '');
    closeCheckInCloseDialog();
  };

  const handleCheckinModalCancel = () => {
    closeCheckInCloseDialog();
  };

  const handleOpenCheckIn = () => {
    updateCheckInStatus(true, id || '');
  };

  const handleCheckinDialogToggle = () => {
    if (!isCheckInOpen) {
      handleOpenCheckIn();
    } else {
      // the close check-in dialog can be skipped if all customers are present
      if (totalBookings === presentBookings) {
        handleCloseCheckIn();
      } else {
        if (showCheckInCloseDialog) {
          handleCloseCheckIn();
        } else {
          openCheckInCloseDialog();
        }
      }
    }
  };

  const eventId = selectedEvent ? selectedEvent.id : id || '';
  const curDomain = currentProvider ? currentProvider.domain : domain || '';
  const redirect = (navigate: Navigate, domain: string, id: string) => (url: string) =>
    navigate(url, { domain, id });
  const redirectTo = redirect(navigate, curDomain, eventId);

  const { differenceBetween } = useDateFns();
  const loadEventGroup = bindActionCreators(requestEventGroupById, dispatch);

  useEffect(() => {
    if (selectedEvent) {
      const eventTimeDifference = differenceBetween(
        new Date().toISOString(),
        selectedEvent.end,
        DifferenceFormat.HOURS,
      );
      if (eventTimeDifference >= 24) {
        updateCheckInStatus(false, id || '');
      }
      loadEventGroup(selectedEvent.eventGroupId, selectedEvent.type);
    }
  }, [selectedEvent]);

  return {
    totalBookings,
    presentBookings,
    handleCheckinModalCancel,
    totalSeats: selectedEvent?.seats,
    showCheckInCloseDialog,
    handleCheckinDialogToggle,
    mobile,
    redirectTo,
    setShowBookingDialog,
    toggleBookingDialog,
    closeBookingDialog,
    showBookingDialog,
  };
};
